import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Button } from "@b2b/ui-components";
import { Mail, KeyRound } from "lucide-react";
import type { TradeRole } from "./hooks/useRegistrationForm";
import type { PricePlanOption } from "./RegistrationForm";
import type { SyncRequest } from "./hooks/useRegistrationForm";
import { RoleSelectionStep } from "./components/RoleSelectionStep";
import RegistrationForm from "./RegistrationForm";
import FormInput from "./components/FormInput";

export interface MultiStepRegistrationProps {
  /** Step 0: Google sign-up. When user completes Google, parent sets token and returns email. */
  googleClientId?: string;
  /** When set (e.g. OAuth redirect), start at step 1 (role selection) with this email and signupMethod "google". */
  prefilledEmailFromOAuth?: string;
  onGoogleCredential?: (
    credential: string,
  ) => Promise<{ email: string } | null>;
  /** Send OTP to email for normal signup (step 1 after email entry). */
  onSendSignupOtp?: (email: string) => Promise<{ success: boolean; message: string }>;
  /** Verify OTP for normal signup before proceeding to role selection. */
  onVerifySignupOtp?: (email: string, otp: string) => Promise<{ success: boolean; message: string }>;
  onRegister?: (
    data: SyncRequest & { password?: string; captchaToken?: string },
  ) => Promise<unknown>;
  onRegistrationSuccess?: (userRole: string) => void;
  onSyncUser?: (syncData: SyncRequest) => Promise<unknown>;
  onGetUserStatus?: () => Promise<{ status: string; user?: unknown }>;
  onCheckShopNameAvailable?: (name: string) => Promise<{ available: boolean }>;
  recaptchaSiteKey?: string;
  pricePlans?: PricePlanOption[];
  onSwitchToSignIn?: () => void;
  /** Notifies parent auth page to resize the card (compact = same as sign-in). */
  onAuthLayoutChange?: (layout: SignupAuthLayout) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Auth page card width: compact matches sign-in; wide fits role cards / registration form. */
export type SignupAuthLayout = "compact" | "wide";

export function resolveSignupAuthLayout(
  step: 0 | 1 | 2 | 3,
  signupMethod: "email" | "google" | null,
): SignupAuthLayout {
  if (step === 0) return "compact";
  if (step === 1 && signupMethod === "email") return "compact";
  return "wide";
}

/** Signup OTP: digits only, max 6 (leading zeros preserved). */
function normalizeOtpDigits(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 6);
}

export const MultiStepRegistration = (props: MultiStepRegistrationProps) => {
  const hasOAuthPrefill = Boolean(
    props.prefilledEmailFromOAuth?.trim(),
  );
  const [step, setStep] = useState<0 | 1 | 2 | 3>(hasOAuthPrefill ? 1 : 0);
  const [signupMethod, setSignupMethod] = useState<"email" | "google" | null>(
    hasOAuthPrefill ? "google" : null,
  );
  const [step0Email, setStep0Email] = useState(
    props.prefilledEmailFromOAuth?.trim() ?? "",
  );
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<TradeRole>("buyer");
  const [step0Error, setStep0Error] = useState<string | null>(null);
  const [emailSendLoading, setEmailSendLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const onGoogleCredentialRef = useRef(props.onGoogleCredential);
  onGoogleCredentialRef.current = props.onGoogleCredential;

  useLayoutEffect(() => {
    props.onAuthLayoutChange?.(resolveSignupAuthLayout(step, signupMethod));
  }, [step, signupMethod, props.onAuthLayoutChange]);

  const handleGoogleCallback = (credential: string) => {
    const fn = onGoogleCredentialRef.current;
    if (!fn) return;
    setGoogleLoading(true);
    setStep0Error(null);
    fn(credential)
      .then((result) => {
        if (result?.email) {
          setStep0Email(result.email);
          setSignupMethod("google");
          setStep(1);
        } else {
          setStep0Error("Could not get email from Google. Please try again.");
        }
      })
      .catch(() => {
        setStep0Error("Google sign-up failed. Please try again.");
      })
      .finally(() => setGoogleLoading(false));
  };

  useEffect(() => {
    if (!props.googleClientId || !googleButtonRef.current) return;
    if (!onGoogleCredentialRef.current) return;

    const container = googleButtonRef.current;
    const clientId = props.googleClientId;

    const initAndRender = () => {
      const g = (window as unknown as { google?: { accounts: { id: { initialize: (c: unknown) => void; renderButton: (p: HTMLElement, o: unknown) => void } } } }).google;
      if (!g?.accounts?.id || !container) return;
      g.accounts.id.initialize({
        client_id: clientId,
        callback: (response: { credential: string }) => {
          handleGoogleCallback(response.credential);
        },
      });
      try {
        g.accounts.id.renderButton(container, {
          theme: "outline",
          size: "large",
          type: "standard",
          text: "signup_with",
        });
      } catch {
        g.accounts.id.renderButton(container, {});
      }
    };

    const g = (window as unknown as { google?: { accounts: { id: unknown } } }).google;
    if (g?.accounts?.id) {
      initAndRender();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => initAndRender();
    document.head.appendChild(script);
    // Intentionally only re-init when googleClientId changes; the credential handler is
    // read via ref so a new parent function reference doesn't tear down Google's iframe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.googleClientId]);

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep0Error(null);
    const trimmed = step0Email.trim();
    if (!trimmed) {
      setStep0Error("Please enter your email address.");
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setStep0Error("Please enter a valid email address.");
      return;
    }
    if (!props.onSendSignupOtp) {
      setStep0Error("Signup is not configured. Please try again later.");
      return;
    }
    setEmailSendLoading(true);
    try {
      await props.onSendSignupOtp(trimmed);
      setSignupMethod("email");
      setOtpValue("");
      setOtpError(null);
      setStep(1);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        "Failed to send verification code. Please try again.";
      setStep0Error(message);
    } finally {
      setEmailSendLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    const code = normalizeOtpDigits(otpValue);
    if (code.length !== 6) {
      setOtpError("Please enter the 6-digit code from your email.");
      return;
    }
    if (!props.onVerifySignupOtp || !step0Email.trim()) return;
    const email = step0Email.trim();
    if (import.meta.env.DEV) {
      console.log("[signup] verify OTP", { email: email.toLowerCase(), otp: code });
    }
    setOtpLoading(true);
    try {
      await props.onVerifySignupOtp(email, code);
      setStep(2);
    } catch (err: unknown) {
      const raw =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
          ?.message ??
        (err as Error)?.message ??
        "Invalid verification code. Please try again.";
      const message = Array.isArray(raw) ? raw.join(" ") : raw;
      const lower = message.toLowerCase();
      if (lower.includes("expired")) {
        setOtpError("Verification code expired. Please request a new one.");
      } else {
        setOtpError(message);
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // Step 0: How to sign up
  if (step === 0) {
    return (
      <div className="w-full min-w-0 space-y-5 sm:space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-bold tracking-tight text-gray-900 sm:text-xl">
            Create your account
          </h3>
          <p className="mt-1.5 text-sm text-gray-600">
            Sign up with Google or use your email address.
          </p>
        </div>

        {props.googleClientId && props.onGoogleCredential && (
          <div className="space-y-4">
            <div
              ref={googleButtonRef}
              className="flex w-full min-w-0 justify-center overflow-hidden [&>div]:!w-full [&>div]:!max-w-full [&>div]:!flex [&>div]:!justify-center [&_iframe]:!max-w-full"
            />
            {googleLoading && (
              <p className="text-center text-sm text-gray-500">
                Signing up with Google...
              </p>
            )}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-[#EDEEF2]" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-3 font-medium text-gray-500 sm:px-4">
                  Or continue with email
                </span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleEmailContinue} className="space-y-5 text-left">
          <FormInput
            id="multi-step-email"
            label="Email address"
            icon={Mail}
            type="email"
            placeholder="Enter your email"
            value={step0Email}
            onChange={(e) => {
              setStep0Email(e.target.value);
              if (step0Error) setStep0Error(null);
            }}
            aria-invalid={!!step0Error}
            aria-describedby={step0Error ? "step0-email-error" : undefined}
            visualVariant="authSignupEmail"
          />
          {step0Error && (
            <p id="step0-email-error" className="text-sm text-red-600" role="alert">
              {step0Error}
            </p>
          )}
          <Button
            type="submit"
            variant="ctaPrimary"
            size="lg"
            className="h-12 w-full rounded-lg bg-[#f15a11] font-bold shadow-md hover:bg-[#e04d0a]"
            disabled={emailSendLoading}
          >
            {emailSendLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Sending code...
              </span>
            ) : (
              "Continue →"
            )}
          </Button>
        </form>
      </div>
    );
  }

  // Step 1: OTP verification (email path only)
  if (step === 1 && signupMethod === "email") {
    return (
      <div className="w-full min-w-0 space-y-5 text-center sm:space-y-6">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">
            Enter verification code
          </h3>
          <p className="mt-1 break-words text-sm text-gray-600">
            We sent a 6-digit code to{" "}
            <strong className="break-all">{step0Email}</strong>. Enter it below.
          </p>
        </div>
        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <FormInput
            id="multi-step-otp"
            label="Verification code"
            icon={KeyRound}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            value={otpValue}
            onChange={(e) => {
              setOtpValue(normalizeOtpDigits(e.target.value));
              if (otpError) setOtpError(null);
            }}
            aria-invalid={!!otpError}
            aria-describedby={otpError ? "step1-otp-error" : undefined}
            visualVariant="authSignin"
          />
          {otpError && (
            <p id="step1-otp-error" className="text-sm text-red-600" role="alert">
              {otpError}
            </p>
          )}
          <Button
            type="submit"
            variant="ctaPrimary"
            size="lg"
            className="h-12 w-full rounded-lg bg-[#f15a11] font-bold shadow-md hover:bg-[#e04d0a]"
            disabled={otpLoading}
          >
            {otpLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Verifying...
              </span>
            ) : (
              "Verify and continue"
            )}
          </Button>
        </form>
        <button
          type="button"
          onClick={() => setStep(0)}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Use a different email
        </button>
      </div>
    );
  }

  // Step 1 (Google) or Step 2 (email): Role selection
  if (step === 1 && signupMethod === "google") {
    return (
      <RoleSelectionStep
        value={selectedRole}
        onChange={setSelectedRole}
        onContinue={() => setStep(2)}
      />
    );
  }
  if (step === 2 && signupMethod === "email") {
    return (
      <RoleSelectionStep
        value={selectedRole}
        onChange={setSelectedRole}
        onContinue={() => setStep(3)}
      />
    );
  }

  // Step 2 (Google) or Step 3 (email): Registration form
  const isOAuthPath = signupMethod === "google";
  return (
    <RegistrationForm
      embeddedInAuthPage
      initialRole={selectedRole}
      prefilledEmail={step0Email || undefined}
      isOAuthPath={isOAuthPath}
      isDirectRegistration={!isOAuthPath}
      onRegister={props.onRegister}
      onRegistrationSuccess={props.onRegistrationSuccess}
      onSyncUser={isOAuthPath ? props.onSyncUser : undefined}
      onGetUserStatus={isOAuthPath ? props.onGetUserStatus : undefined}
      onCheckShopNameAvailable={props.onCheckShopNameAvailable}
      onSwitchToSignIn={props.onSwitchToSignIn}
      recaptchaSiteKey={isOAuthPath ? undefined : props.recaptchaSiteKey}
      pricePlans={props.pricePlans}
    />
  );
};
